import type { ReactElement } from 'react';
import { NeonRadioField } from '@ps-refarch-ux/neon';
import type { RadioOption } from '@ps-refarch-ux/neon';
import {
  APPROVAL_REQUIREMENTS_LABEL,
  APPROVAL_REQUIREMENT_OPTIONS,
  STUDENT_SUBMISSION_RULES_LABEL,
  STUDENT_SUBMISSION_OPTIONS,
  STAFF_APPROVAL_RULES_LABEL,
  STAFF_APPROVAL_OPTIONS,
} from '@/constants/configurations-constants';
import type { ApprovalsFormState } from '@/constants/configurations-constants';
import {
  tabContentBlockStyle,
  sectionBlockStyle,
  sectionHeadingStyle,
} from './configurations.styles';

const approvalRequirementOptions: RadioOption[] = APPROVAL_REQUIREMENT_OPTIONS.map((o) => ({
  id: o.id,
  labelText: o.labelText,
  value: o.value,
}));
const studentSubmissionOptions: RadioOption[] = STUDENT_SUBMISSION_OPTIONS.map((o) => ({
  id: o.id,
  labelText: o.labelText,
  value: o.value,
}));
const staffApprovalOptions: RadioOption[] = STAFF_APPROVAL_OPTIONS.map((o) => ({
  id: o.id,
  labelText: o.labelText,
  value: o.value,
}));

type ApprovalsTabProps = {
  formState: ApprovalsFormState;
  onFormChange: (state: ApprovalsFormState) => void;
  showStudentSubmissionRules: boolean;
  showStaffApprovalRules: boolean;
};

export function ApprovalsTab({
  formState,
  onFormChange,
  showStudentSubmissionRules,
  showStaffApprovalRules,
}: ApprovalsTabProps): ReactElement {
  const setField = {
    approvalRequirement: (value: string) =>
      onFormChange({ ...formState, approvalRequirement: value }),
    studentSubmission: (value: string) => onFormChange({ ...formState, studentSubmission: value }),
    staffApproval: (value: string) => onFormChange({ ...formState, staffApproval: value }),
  };

  return (
    <div style={tabContentBlockStyle}>
      <section style={sectionBlockStyle} aria-labelledby="heading-approval-requirements">
        <h2 id="heading-approval-requirements" style={sectionHeadingStyle}>
          {APPROVAL_REQUIREMENTS_LABEL}
        </h2>
        <NeonRadioField
          id="approval-requirements-field"
          dataLabelText={APPROVAL_REQUIREMENTS_LABEL}
          dataShowLabel={false}
          modelValue={formState.approvalRequirement}
          modelValueChange={(value) => value !== undefined && setField.approvalRequirement(value)}
          options={approvalRequirementOptions}
          dataControlLayout="column"
        />
      </section>

      {showStudentSubmissionRules ? (
        <section style={sectionBlockStyle} aria-labelledby="heading-student-submission">
          <h2 id="heading-student-submission" style={sectionHeadingStyle}>
            {STUDENT_SUBMISSION_RULES_LABEL}
          </h2>
          <NeonRadioField
            id="student-submission-rules-field"
            dataLabelText={STUDENT_SUBMISSION_RULES_LABEL}
            dataShowLabel={false}
            modelValue={formState.studentSubmission}
            modelValueChange={(value) => value !== undefined && setField.studentSubmission(value)}
            options={studentSubmissionOptions}
            dataControlLayout="column"
          />
        </section>
      ) : null}

      {showStaffApprovalRules ? (
        <section style={sectionBlockStyle} aria-labelledby="heading-staff-approval">
          <h2 id="heading-staff-approval" style={sectionHeadingStyle}>
            {STAFF_APPROVAL_RULES_LABEL}
          </h2>
          <NeonRadioField
            id="staff-approval-rules-field"
            dataLabelText={STAFF_APPROVAL_RULES_LABEL}
            dataShowLabel={false}
            modelValue={formState.staffApproval}
            modelValueChange={(value) => value !== undefined && setField.staffApproval(value)}
            options={staffApprovalOptions}
            dataControlLayout="column"
          />
        </section>
      ) : null}
    </div>
  );
}
